import { useEffect, useState } from 'react';
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash, Plus, Pause, Play, X as XIcon, ArrowsClockwise } from '@phosphor-icons/react';
import { api } from '@/lib/api';
import type {
  Campaign,
  CampaignRun,
  CampaignRunRow,
  CampaignStep,
  Template,
} from '@/lib/types';
import { cn } from '@/lib/utils';

export default function Campaigns() {
  return (
    <Routes>
      <Route index element={<CampaignsList />} />
      <Route path="new" element={<CampaignEditor />} />
      <Route path=":id" element={<CampaignDetail />} />
      <Route path=":id/edit" element={<CampaignEditor />} />
    </Routes>
  );
}

/* -------------------------------------------------------------------------- */
/*  List                                                                       */
/* -------------------------------------------------------------------------- */

function CampaignsList() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => api<{ ok: true; items: Campaign[] }>('/api/campaigns'),
  });
  const items = query.data?.items || [];

  const tick = useMutation({
    mutationFn: () => api<{ ok: true; processed: number; errors: number }>('/api/campaigns/_tick', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });

  return (
    <div className="space-y-4 max-w-4xl">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Kampaně</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Kampaň = sekvence šablon s odstupy ve dnech. Scheduler kontroluje každou minutu, jestli
            má některý běh splatný krok, a vygeneruje dopisy všem eligible vlastníkům.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => tick.mutate()}
            disabled={tick.isPending}
            title="Vynutit kontrolu scheduleru hned"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs hover:bg-muted disabled:opacity-50"
          >
            <ArrowsClockwise size={12} className={tick.isPending ? 'animate-spin' : ''} />
            Spustit tick
          </button>
          <Link
            to="/campaigns/new"
            className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium"
          >
            + Nová kampaň
          </Link>
        </div>
      </header>

      {query.isLoading && <p className="text-sm text-muted-foreground">Načítám…</p>}

      {items.length === 0 && !query.isLoading && (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          Zatím žádné kampaně. Vytvoř první a pak v Nemovitostech vyber, koho do ní přidat.
        </div>
      )}

      {items.length > 0 && (
        <ul className="rounded-lg border divide-y">
          {items.map((c) => (
            <li key={c.id} className="p-4 hover:bg-muted/30 flex items-center gap-3">
              <Link to={`/campaigns/${c.id}`} className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{c.name}</span>
                  {!c.active && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground border">
                      vypnutá
                    </span>
                  )}
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                    {c.steps.length} {c.steps.length === 1 ? 'krok' : c.steps.length < 5 ? 'kroky' : 'kroků'}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  upraveno {new Date(c.updatedAt).toLocaleDateString('cs-CZ')}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Editor                                                                     */
/* -------------------------------------------------------------------------- */

function CampaignEditor() {
  const { id } = useParams();
  const isEditing = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [active, setActive] = useState(true);
  const [steps, setSteps] = useState<CampaignStep[]>([{ templateId: '', delayDays: 0 }]);
  const [initialized, setInitialized] = useState(false);

  const tplQuery = useQuery({
    queryKey: ['templates'],
    queryFn: () => api<{ ok: true; items: Template[] }>('/api/templates'),
  });
  const templates = tplQuery.data?.items || [];

  const cQuery = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => api<{ ok: true; campaign: Campaign }>(`/api/campaigns/${id}`),
    enabled: isEditing,
  });

  useEffect(() => {
    if (cQuery.data?.campaign && !initialized) {
      const c = cQuery.data.campaign;
      setName(c.name);
      setActive(c.active);
      setSteps(c.steps.length > 0 ? c.steps : [{ templateId: '', delayDays: 0 }]);
      setInitialized(true);
    }
  }, [cQuery.data, initialized]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = { name, active, steps };
      if (isEditing) {
        return api<{ ok: true; campaign: Campaign }>(`/api/campaigns/${id}`, {
          method: 'PUT',
          json: payload,
        });
      }
      return api<{ ok: true; id: string }>('/api/campaigns', {
        method: 'POST',
        json: payload,
      });
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      const newId = (data as { id?: string; campaign?: { id: string } }).id
        || (data as { campaign?: { id: string } }).campaign?.id;
      navigate(newId ? `/campaigns/${newId}` : '/campaigns');
    },
  });

  function updateStep(i: number, patch: Partial<CampaignStep>) {
    setSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  }
  function addStep() {
    setSteps((prev) => [...prev, { templateId: prev[0]?.templateId || '', delayDays: 14 }]);
  }
  function removeStep(i: number) {
    setSteps((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));
  }

  const canSave = name.trim().length > 0 && steps.every((s) => s.templateId);

  return (
    <div className="space-y-5 max-w-3xl">
      <header>
        <div className="text-xs text-muted-foreground">
          <Link to="/campaigns" className="hover:underline">Kampaně</Link> / {isEditing ? name || '…' : 'Nová'}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight mt-1">
          {isEditing ? 'Upravit kampaň' : 'Nová kampaň'}
        </h1>
      </header>

      <div className="space-y-1.5">
        <label className="text-sm font-medium">Název</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={'např. „Distress — 3 dopisy s odstupem 14 dnů"'}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
        />
      </div>

      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
        Aktivní (jinak scheduler kampaň ignoruje)
      </label>

      <div className="space-y-3">
        <div className="text-sm font-medium">Kroky</div>
        <p className="text-xs text-muted-foreground -mt-1">
          Krok 1 spustí scheduler po jeho prvním proběhu po enrollmentu (typicky do minuty pokud delay = 0).
          Další kroky se posouvají o nastavený počet dnů od předchozího kroku.
        </p>
        <ol className="space-y-2">
          {steps.map((s, i) => (
            <li key={i} className="flex items-center gap-2 rounded-md border p-2">
              <span className="text-xs font-mono text-muted-foreground w-6 text-center">{i + 1}</span>
              <select
                value={s.templateId}
                onChange={(e) => updateStep(i, { templateId: e.target.value })}
                className="flex-1 rounded-md border bg-background px-2 py-1.5 text-sm"
              >
                <option value="">— vyber šablonu —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                {i === 0 ? 'Start +' : 'Po předchozím +'}
                <input
                  type="number"
                  min={0}
                  value={s.delayDays}
                  onChange={(e) => updateStep(i, { delayDays: Math.max(0, Number(e.target.value) || 0) })}
                  className="w-16 rounded-md border bg-background px-2 py-1 text-sm text-right"
                />
                dnů
              </label>
              {steps.length > 1 && (
                <button
                  onClick={() => removeStep(i)}
                  className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                  title="Smazat krok"
                >
                  <Trash size={14} />
                </button>
              )}
            </li>
          ))}
        </ol>
        <button
          onClick={addStep}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs hover:bg-muted"
        >
          <Plus size={12} /> Přidat krok
        </button>
      </div>

      <div className="flex items-center gap-3 pt-3 border-t">
        <Link to={isEditing ? `/campaigns/${id}` : '/campaigns'} className="px-4 py-2 rounded-md border text-sm hover:bg-muted">
          Zpět
        </Link>
        <button
          onClick={() => save.mutate()}
          disabled={!canSave || save.isPending}
          className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
        >
          {save.isPending ? 'Ukládám…' : isEditing ? 'Uložit' : 'Vytvořit'}
        </button>
        {save.error && (
          <span className="text-xs text-destructive">Chyba: {String(save.error)}</span>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Detail (běžící runs)                                                       */
/* -------------------------------------------------------------------------- */

function CampaignDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirmDel, setConfirmDel] = useState(false);

  const query = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => api<{ ok: true; campaign: Campaign; runs: CampaignRunRow[] }>(`/api/campaigns/${id}`),
    enabled: !!id,
    refetchInterval: 30_000,
  });

  const tplQuery = useQuery({
    queryKey: ['templates'],
    queryFn: () => api<{ ok: true; items: Template[] }>('/api/templates'),
  });
  const tplName = (tid: string) => tplQuery.data?.items.find((t) => t.id === tid)?.name || tid.slice(0, 8);

  const del = useMutation({
    mutationFn: () => api(`/api/campaigns/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      navigate('/campaigns');
    },
  });

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Načítám…</p>;
  if (!query.data) return null;
  const { campaign, runs } = query.data;

  return (
    <div className="space-y-5 max-w-4xl">
      <header className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-muted-foreground">
            <Link to="/campaigns" className="hover:underline">Kampaně</Link> / {campaign.name}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1">{campaign.name}</h1>
          <div className="text-sm text-muted-foreground mt-1">
            {campaign.active ? 'Aktivní' : 'Vypnutá'} · {campaign.steps.length} kroků · {runs.length} běhů
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Link to={`/campaigns/${id}/edit`} className="px-3 py-1.5 rounded-md border text-sm hover:bg-muted">
            Upravit
          </Link>
          {!confirmDel ? (
            <button
              onClick={() => setConfirmDel(true)}
              className="px-3 py-1.5 rounded-md border border-destructive text-destructive text-sm hover:bg-destructive/10"
            >
              Smazat
            </button>
          ) : (
            <>
              <button
                onClick={() => del.mutate()}
                disabled={del.isPending}
                className="px-3 py-1.5 rounded-md bg-destructive text-destructive-foreground text-sm disabled:opacity-50"
              >
                {del.isPending ? 'Mažu…' : 'Smazat — všechny běhy'}
              </button>
              <button onClick={() => setConfirmDel(false)} className="px-3 py-1.5 rounded-md border text-sm">
                Zrušit
              </button>
            </>
          )}
        </div>
      </header>

      <section className="rounded-lg border">
        <div className="px-4 py-2.5 border-b bg-muted/40 text-sm font-medium">Kroky kampaně</div>
        <ol className="divide-y">
          {campaign.steps.map((s, i) => (
            <li key={i} className="px-4 py-2 flex items-center gap-3 text-sm">
              <span className="text-xs font-mono text-muted-foreground w-6 text-center">{i + 1}</span>
              <span className="flex-1">{tplName(s.templateId)}</span>
              <span className="text-xs text-muted-foreground tabular-nums">+{s.delayDays} dnů</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="rounded-lg border">
        <div className="px-4 py-2.5 border-b bg-muted/40 text-sm font-medium flex items-center justify-between">
          <span>Běhy ({runs.length})</span>
          <span className="text-xs text-muted-foreground">Auto-refresh každých 30 s</span>
        </div>
        {runs.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground italic">
            Žádné běhy. Přidej nemovitosti přes seznam Nemovitosti → Vybrat → Přidat do kampaně.
          </div>
        ) : (
          <ul className="divide-y text-sm">
            {runs.map(({ run, property }) => (
              <RunRow key={run.id} run={run} property={property} stepCount={campaign.steps.length} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function RunRow({
  run,
  property,
  stepCount,
}: {
  run: CampaignRun;
  property: { id: string; obec: string | null; parcela: string | null };
  stepCount: number;
}) {
  const qc = useQueryClient();
  const updateStatus = useMutation({
    mutationFn: (status: 'active' | 'paused' | 'stopped') =>
      api(`/api/campaign-runs/${run.id}`, { method: 'PATCH', json: { status } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign'] }),
  });
  const del = useMutation({
    mutationFn: () => api(`/api/campaign-runs/${run.id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign'] }),
  });

  const progress = run.currentStep === -1 ? 'čeká na start' : `${run.currentStep + 1} / ${stepCount}`;
  const nextLabel = run.nextSendAt
    ? `další ${new Date(run.nextSendAt).toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' })}`
    : run.status === 'done'
      ? 'dokončeno'
      : '—';

  const statusColor: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-900 border-emerald-200',
    paused: 'bg-amber-100 text-amber-900 border-amber-200',
    done: 'bg-slate-100 text-slate-700 border-slate-200',
    stopped: 'bg-destructive/10 text-destructive border-destructive/40',
  };

  return (
    <li className="px-4 py-2.5 flex items-center gap-3">
      <Link to={`/properties/${property.id}`} className="flex-1 min-w-0 hover:underline">
        <div className="font-medium truncate">{property.obec || property.id.slice(0, 8)}</div>
        <div className="text-xs text-muted-foreground">parcela {property.parcela || '—'}</div>
      </Link>
      <div className="text-xs text-muted-foreground text-right tabular-nums w-40">
        <div>{progress}</div>
        <div>{nextLabel}</div>
      </div>
      <span
        className={cn(
          'text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border whitespace-nowrap',
          statusColor[run.status],
        )}
      >
        {run.status}
      </span>
      <div className="flex items-center gap-1 shrink-0">
        {run.status === 'active' && (
          <button
            onClick={() => updateStatus.mutate('paused')}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground"
            title="Pozastavit"
          >
            <Pause size={13} />
          </button>
        )}
        {run.status === 'paused' && (
          <button
            onClick={() => updateStatus.mutate('active')}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground"
            title="Pokračovat"
          >
            <Play size={13} />
          </button>
        )}
        <button
          onClick={() => del.mutate()}
          className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
          title="Odebrat z kampaně"
        >
          <XIcon size={13} />
        </button>
      </div>
    </li>
  );
}
